/**
 * Участники ПРОСТРАНСТВА: список + удаление.
 *
 * Правило Антона 12.09 11:56: «when editing should be able to remove
 * members. Removing members automatically disconnects them from all the
 * rooms but not deleting any rooms. Rooms with only 2 participants will
 * remain with 1 participant and full history.»
 *
 * Отдельный компонент, а не вкладка внутри RoomSettings: RoomSettings —
 * это настройки ОДНОЙ комнаты (имя/тема/аватар/доступ), а здесь действие
 * затрагивает сразу дерево комнат. Смешивать их в одном экране значит
 * сделать «удалить» рядом с «сменить аватар», хотя цена ошибки разная.
 */
import { useMemo, useState } from "react";
import { getLangSnapshot, useT, useTF } from "../i18n";
import type { MatrixClient } from "matrix-js-sdk";

import { membersOf, removeFromSpace, humanError } from "../matrix/roomActions";
import { Dialog, DialogButton } from "./Dialog";
import { color, font, space as sp } from "./theme";

/**
 * Форма фразы «выйдет из пространства и из N комнат внутри него».
 * В русском три формы, в остальных языках две — выбор формы в коде,
 * слова в словаре (образец — namesMoreKey в src/matrix/timeline.ts).
 */
function leaveRoomsKey(
  n: number,
): "space.leaveRoomsOne" | "space.leaveRoomsFew" | "space.leaveRoomsMany" {
  if (getLangSnapshot() !== "ru") {
    return n === 1 ? "space.leaveRoomsOne" : "space.leaveRoomsMany";
  }
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return "space.leaveRoomsMany";
  const mod10 = n % 10;
  if (mod10 === 1) return "space.leaveRoomsOne";
  if (mod10 >= 2 && mod10 <= 4) return "space.leaveRoomsFew";
  return "space.leaveRoomsMany";
}

/** Комнаты пространства, где этот человек реально состоит. */
function roomsWithUser(client: MatrixClient, spaceId: string, userId: string) {
  const space = client.getRoom(spaceId);
  if (!space) return [];
  const childIds = space.currentState
    .getStateEvents("m.space.child")
    .filter((ev) => Object.keys(ev.getContent()).length > 0)
    .map((ev) => ev.getStateKey())
    .filter((id): id is string => Boolean(id));

  return childIds
    .map((id) => client.getRoom(id))
    .filter((r): r is NonNullable<typeof r> => Boolean(r))
    .filter((r) => {
      const m = r.getMember(userId);
      return m?.membership === "join" || m?.membership === "invite";
    });
}

export function SpaceMembers({
  client,
  spaceId,
  onClose,
}: {
  client: MatrixClient;
  spaceId: string;
  onClose: () => void;
}) {
  const t = useT();
  const tfmt = useTF();
  const space = client.getRoom(spaceId);
  const [confirm, setConfirm] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const me = client.getUserId();
  const members = useMemo(
    () => (space ? membersOf(client, space) : []),
    // tick — принудительный пересчёт после удаления: state-событие может
    // прийти с задержкой, а список должен обновиться сразу.
    [client, space, tick],
  );
  const myLevel = me && space ? (space.getMember(me)?.powerLevel ?? 0) : 0;

  if (!space) {
    return (
      <Dialog title={t("room.spaceMembers")} onClose={onClose} footer={null}>{t("space.membersNotFound")}</Dialog>
    );
  }

  const target = confirm ? members.find((m) => m.userId === confirm) : null;
  const targetRooms = confirm ? roomsWithUser(client, spaceId, confirm) : [];

  const doRemove = async (userId: string) => {
    setBusy(true);
    setError(null);
    try {
      const rooms = roomsWithUser(client, spaceId, userId);
      const { failed } = await removeFromSpace(
        client,
        spaceId,
        userId,
        rooms.map((r) => r.roomId),
      );
      setConfirm(null);
      setTick((t) => t + 1);
      if (failed.length > 0) {
        // Частичный результат называем поимённо: «готово» при половине
        // закрытых комнат — это ложь, из-за которой человек решит, что
        // доступ закрыт полностью.
        setError(
          t("ui.udaleno_ne_vezde_ostalos") +
            failed.map((f) => `${f.roomName} (${f.reason})`).join("; "),
        );
      } else {
        setDone(tfmt("space.removed", { user: userId, count: rooms.length }));
      }
    } catch (e) {
      setError(humanError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={tfmt("space.membersTitle", { space: space.name || space.roomId })}
      onClose={onClose}
      error={error}
      footer={
        <DialogButton onClick={onClose} testId="space-members-close">{t("common.close")}</DialogButton>
      }
    >
      {done && (
        <div style={{ fontSize: font.micro, color: color.textDim, marginBottom: sp.sm }}>
          {done}
        </div>
      )}

      {confirm && target ? (
        <div style={{ fontSize: font.body, lineHeight: 1.45 }}>{t("common.remove")}<b>{target.name}</b>{t("space.fromSpaceQuestion")}<div style={{ marginTop: sp.sm, color: color.textDim, fontSize: font.micro }}>
            {/* Прямо перечисляем последствия: человек должен видеть объём
                до нажатия, а не узнавать его из результата. */}
            {tfmt(leaveRoomsKey(targetRooms.length), { count: targetRooms.length })}
            {targetRooms.length > 0 && (
              <>: {targetRooms.map((r) => r.name || r.roomId).join(", ")}</>
            )}
            {t("space.removeMemberTail")}
          </div>
          <div style={{ display: "flex", gap: sp.sm, marginTop: sp.md }}>
            <DialogButton onClick={() => setConfirm(null)} testId="space-remove-cancel">{t("common.cancel")}</DialogButton>
            <DialogButton
              kind="danger"
              disabled={busy}
              onClick={() => void doRemove(confirm)}
              testId="space-remove-confirm"
            >
              {busy ? t("ui.udalyayu") : t("common.remove")}
            </DialogButton>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {members.map((m) => {
            // Удалять может тот, чей уровень ВЫШЕ: равного по правам
            // сервер не даст кикнуть, и предлагать это бессмысленно.
            const canRemove = m.userId !== me && myLevel > m.powerLevel && myLevel >= 50;
            return (
              <div
                key={m.userId}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: sp.sm,
                  padding: "6px 0",
                }}
              >
                <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                  <div style={{ fontSize: font.body }}>
                    {m.name}
                    {m.userId === me && (
                      <span style={{ color: color.textDim, fontSize: font.micro }}>{t("space.itIsYou")}</span>
                    )}
                    {m.powerLevel >= 100 && (
                      <span style={{ color: color.textDim, fontSize: font.micro }}>{t("space.adminSuffix")}</span>
                    )}
                    {m.membership === "invite" && (
                      <span style={{ color: color.textDim, fontSize: font.micro }}>
                        {" "}{t("space.invitedSuffix")}</span>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: font.micro,
                      color: color.textDim,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {m.userId}
                  </div>
                </div>
                {canRemove && (
                  <button
                    type="button"
                    onClick={() => setConfirm(m.userId)}
                    data-testid="space-member-remove"
                    style={{
                      font: "inherit",
                      fontSize: font.micro,
                      color: color.danger,
                      background: "transparent",
                      border: `1px solid ${color.border}`,
                      borderRadius: 6,
                      padding: "3px 8px",
                      cursor: "pointer",
                      flex: "0 0 auto",
                    }}
                  >{t("common.remove")}</button>
                )}
              </div>
            );
          })}
          {members.length === 0 && (
            <div style={{ color: color.textDim, fontSize: font.micro }}>{t("space.noMembersYet")}</div>
          )}
        </div>
      )}
    </Dialog>
  );
}
